/**
 * Temporizador del paso.
 *
 * El `setInterval` de aquí SOLO sirve para repintar. La cuenta sale siempre de
 * los timestamps persistidos, así que si el intervalo se estrangula, se para o
 * el proceso muere, el número sigue siendo correcto al volver.
 *
 * El tiempo es SUGERIDO: superarlo no bloquea nada y se dice con todas las
 * letras.
 */

import { useEffect, useState } from 'react';

import type { PasoRegistrado } from '../../domain/modelo/sesion';
import { segundosDePaso, segundosRestantes, temporizadorCorriendo } from '../../domain/reglas/sesionActiva';
import estilos from './Temporizador.module.css';

interface Props {
  paso: PasoRegistrado;
  minutosSugeridos: number | null;
  onIniciar: () => void;
  onPausar: () => void;
  onReiniciar: () => void;
}

function formatear(segundos: number): string {
  const signo = segundos < 0 ? '−' : '';
  const abs = Math.abs(segundos);
  const minutos = Math.floor(abs / 60);
  const resto = abs % 60;
  return `${signo}${String(minutos).padStart(2, '0')}:${String(resto).padStart(2, '0')}`;
}

export function Temporizador({
  paso,
  minutosSugeridos,
  onIniciar,
  onPausar,
  onReiniciar,
}: Props) {
  const corriendo = temporizadorCorriendo(paso);
  const [, repintar] = useState(0);

  useEffect(() => {
    if (!corriendo) return;
    const id = setInterval(() => repintar((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [corriendo]);

  if (minutosSugeridos === null) {
    return (
      <p className={estilos.sinTiempo}>Este paso no tiene tiempo sugerido.</p>
    );
  }

  const restante = segundosRestantes(paso, minutosSugeridos) ?? 0;
  const transcurrido = segundosDePaso(paso);
  const superado = restante < 0;

  return (
    <div className={estilos.temporizador}>
      <div className={estilos.pantalla} data-superado={superado || undefined}>
        <span className={estilos.numero} aria-live="off">
          {formatear(restante)}
        </span>
        <span className={estilos.leyenda}>
          {superado
            ? `tiempo sugerido superado · llevás ${formatear(transcurrido)}`
            : `de ${minutosSugeridos} min sugeridos`}
        </span>
      </div>

      <div className={estilos.controles}>
        {corriendo ? (
          <button type="button" className={estilos.boton} onClick={onPausar}>
            Pausar
          </button>
        ) : (
          <button type="button" className={estilos.boton} onClick={onIniciar}>
            {transcurrido > 0 ? 'Continuar' : 'Iniciar'}
          </button>
        )}
        <button
          type="button"
          className={estilos.boton}
          onClick={onReiniciar}
          disabled={transcurrido === 0 && !corriendo}
        >
          Reiniciar
        </button>
      </div>

      <p className={estilos.nota}>
        El tiempo es sugerido. Si terminás antes, avanzá.
      </p>
    </div>
  );
}
